import type { FileUIPart } from "ai"

const PDF_MEDIA_TYPE = "application/pdf"
const CSV_MEDIA_TYPE = "text/csv"
const UNSUPPORTED_MEDIA_TYPE = "application/octet-stream"

export const AI_CHAT_ATTACHMENT_FILE_ACCEPT = ["image/*", ".pdf", ".csv"].join(
    ","
)

export interface ChatUploadedFile {
    readonly url: string
    readonly file_name?: string | null
    readonly mime_type?: string | null
}

export interface ChatFileAttachment {
    readonly id: string
    readonly url: string
    readonly filename: string
    readonly mediaType: string
}

export function isImageAttachmentMediaType(mediaType: string) {
    return mediaType.startsWith("image/")
}

export function isPdfAttachmentMediaType(mediaType: string) {
    return mediaType === PDF_MEDIA_TYPE
}

function isCsvAttachmentMediaType(mediaType: string) {
    return mediaType === CSV_MEDIA_TYPE || mediaType === "application/csv"
}

export type ChatAttachmentPreviewKind = "image" | "file"

export function getChatAttachmentPreviewKind(
    mediaType: string
): ChatAttachmentPreviewKind {
    if (isImageAttachmentMediaType(mediaType)) return "image"
    return "file"
}

export function isSupportedAiChatAttachmentMediaType(mediaType: string) {
    return (
        isImageAttachmentMediaType(mediaType) ||
        isPdfAttachmentMediaType(mediaType) ||
        isCsvAttachmentMediaType(mediaType)
    )
}

export function toChatFileAttachment(
    file: ChatUploadedFile
): ChatFileAttachment | null {
    const mediaType = resolveMediaType(file)
    if (!isSupportedAiChatAttachmentMediaType(mediaType)) return null

    return {
        id: file.url,
        url: file.url,
        filename: file.file_name?.trim() || getFallbackFilename(mediaType),
        mediaType,
    }
}

// Browsers can leave `File.type` empty for files copied from the OS file
// manager, so fall back to extension sniffing like `toChatFileAttachment`.
export function resolvePastedFileMediaType(file: File) {
    const mediaTypeFromFilename = inferMediaTypeFromFilename(file.name)
    if (mediaTypeFromFilename !== UNSUPPORTED_MEDIA_TYPE) {
        return mediaTypeFromFilename
    }

    return file.type || UNSUPPORTED_MEDIA_TYPE
}

export function toFileUIPart(attachment: ChatFileAttachment): FileUIPart {
    return {
        type: "file",
        url: attachment.url,
        mediaType: attachment.mediaType,
        filename: attachment.filename,
    }
}

function resolveMediaType(file: ChatUploadedFile) {
    const declaredMediaType = file.mime_type?.trim()
    if (declaredMediaType) return declaredMediaType

    const fromFilename = inferMediaTypeFromFilename(file.file_name)
    if (fromFilename !== UNSUPPORTED_MEDIA_TYPE) return fromFilename

    return inferMediaTypeFromFilename(extractFilenameFromUrl(file.url))
}

function inferMediaTypeFromFilename(filename: string | null | undefined) {
    const normalizedFilename = filename?.trim().toLowerCase() ?? ""
    if (normalizedFilename.endsWith(".pdf")) return PDF_MEDIA_TYPE
    if (normalizedFilename.endsWith(".csv")) return CSV_MEDIA_TYPE
    if (normalizedFilename.endsWith(".png")) return "image/png"
    if (normalizedFilename.endsWith(".webp")) return "image/webp"
    if (normalizedFilename.endsWith(".gif")) return "image/gif"
    if (
        normalizedFilename.endsWith(".jpg") ||
        normalizedFilename.endsWith(".jpeg")
    ) {
        return "image/jpeg"
    }

    return UNSUPPORTED_MEDIA_TYPE
}

function extractFilenameFromUrl(url: string) {
    try {
        const pathname = new URL(url).pathname
        return pathname.split("/").pop() ?? ""
    } catch {
        return url.split("/").pop() ?? ""
    }
}

function getFallbackFilename(mediaType: string) {
    if (mediaType === PDF_MEDIA_TYPE) return "attachment.pdf"
    if (isCsvAttachmentMediaType(mediaType)) return "attachment.csv"
    return "attachment"
}
